import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { FaGlobe, FaTimes, FaCheckSquare, FaSquare, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

export default function QuickWebsiteModal({ isOpen, user, onClose, onUpdated }) {
  const { user: currentUser } = useAuth();
  const [websites, setWebsites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isOpen || !user) return;
    setError('');
    setSuccess('');

    const fetchUserWebsites = async () => {
      try {
        const res = await api.get(`/users/${user.userid}/websites`);
        if (res.data.success) {
          setWebsites(res.data.websites || []);
        }
      } catch (err) {
        console.error('Failed to load user websites:', err);
      }
    };

    fetchUserWebsites();
  }, [isOpen, user]);

  if (!isOpen || !user || currentUser?.usertype !== 0) return null;

  const toggleSite = (websiteId) => {
    setWebsites((prev) =>
      prev.map((w) =>
        w.id === websiteId ? { ...w, can_access: w.can_access ? 0 : 1 } : w
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.put(`/users/${user.userid}/websites`, {
        websites: websites.map((w) => ({
          website_id: w.id,
          can_access: w.can_access,
        })),
      });

      if (res.data.success) {
        setSuccess('Website access permissions updated successfully!');
        if (onUpdated) onUpdated();
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 1200);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update website permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '480px' }}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(14, 165, 233, 0.2)',
                color: 'var(--secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FaGlobe />
            </div>
            <div>
              <h3 style={{ fontSize: '1.15rem' }}>Manage Website Portals</h3>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                User: <strong style={{ color: 'var(--text-main)' }}>{user.name}</strong> (@{user.username})
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.2rem',
            }}
          >
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                color: '#991b1b',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
              }}
            >
              <FaExclamationTriangle />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: '#f0fdf4',
                border: '1px solid #86efac',
                color: '#15803d',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
              }}
            >
              <FaCheckCircle />
              <span>{success}</span>
            </div>
          )}

          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '14px' }}>
            Check the website portals that this user is authorized to access:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {websites.map((w) => {
              const isChecked = Boolean(w.can_access);
              return (
                <div
                  key={w.id}
                  onClick={() => toggleSite(w.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderRadius: 'var(--radius-md)',
                    background: isChecked ? '#eff6ff' : '#ffffff',
                    border: `1px solid ${isChecked ? '#93c5fd' : 'var(--border-color)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ color: isChecked ? 'var(--primary)' : 'var(--text-dim)', fontSize: '1.2rem' }}>
                      {isChecked ? <FaCheckSquare /> : <FaSquare />}
                    </span>
                    <div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-main)' }}>
                        {w.website_name}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                        Code: {w.website_code}
                      </div>
                    </div>
                  </div>
                  <span
                    className={`badge ${isChecked ? 'badge-active' : 'badge-inactive'}`}
                    style={{ fontSize: '0.7rem' }}
                  >
                    {isChecked ? 'Allowed' : 'Disabled'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Website Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
}
